import { Navigate, Route, Routes } from 'react-router-dom';
import React from 'react';
import { ALL_FORMS_PATH, CREATE_FORM_PATH, EDIT_FORM_PATH, LOGIN_APP_PATH } from '../consts/consts';
import Authorization from './Authorization';
import AllFormsMetadata from './FormConsole/AllFormsMetadata';
import EditFormTemplate from './FormConsole/EditFormTemplate';
import { FormMetadata, FormTemplate, Question, StringsFile } from '../types';
import { normalizeFormLanguageCodes } from '../utils/languageMapping';
import { DEFAULT_LANG } from '../LanguageContext';

type SetStringsByLang = React.Dispatch<React.SetStateAction<Record<string, StringsFile>>>;

interface AppRoutesProps {
  showVerification: boolean;
  setShowVerification: (value: boolean) => void;
  inputPhoneOrEmail: string;
  setInputPhoneOrEmail: (value: string) => void;
  sessionId: string;
  setSessionId: (value: string) => void;
  logout: () => void;
  formsMetadata: FormMetadata[] | null;
  setFormsMetadata: (value: FormMetadata[] | null) => void;
  formTemplate: FormTemplate;
  setFormTemplate: (template: FormTemplate) => void;
  stringsByLang: Record<string, StringsFile>;
  setStringsByLang: SetStringsByLang;
  loadedStringsByLang: Record<string, StringsFile>;
  failedLangs: string[];
  loadingForm: boolean;
  verificationError: string;
  setVerificationError: (value: string) => void;
  allQuestions: Question[];
  setAllQuestions: (questions: Question[]) => void;
}

type EditFormRouteProps = {
  formTemplate: FormTemplate;
  setFormTemplate: (template: FormTemplate) => void;
  stringsByLang: Record<string, StringsFile>;
  setStringsByLang: SetStringsByLang;
  loadedStringsByLang: Record<string, StringsFile>;
  failedLangs: string[];
  logout: () => void;
  setFormsMetadata: (value: FormMetadata[] | null) => void;
  allQuestions: Question[];
  languagesForForm: (formId: string | undefined) => string[];
  createForm: boolean;
};

const EditFormRoute: React.FC<EditFormRouteProps> = ({
  formTemplate,
  setFormTemplate,
  stringsByLang,
  setStringsByLang,
  loadedStringsByLang,
  failedLangs,
  logout,
  setFormsMetadata,
  allQuestions,
  languagesForForm,
  createForm,
}) => {
  const formId = formTemplate?.form_id;
  return (
    <EditFormTemplate
      formTemplate={formTemplate}
      setFormTemplate={setFormTemplate}
      stringsByLang={stringsByLang}
      setStringsByLang={setStringsByLang}
      loadedStringsByLang={loadedStringsByLang}
      failedLangs={failedLangs}
      logout={logout}
      setFormsMetadata={setFormsMetadata}
      createForm={createForm}
      allQuestions={allQuestions}
      formLanguages={languagesForForm(formId)}
    />
  );
};

const AppRoutes: React.FC<AppRoutesProps> = (props) => {
  const loginElement = (
    <Authorization
      showVerification={props.showVerification}
      setShowVerification={props.setShowVerification}
      inputPhoneOrEmail={props.inputPhoneOrEmail}
      setInputPhoneOrEmail={props.setInputPhoneOrEmail}
      sessionId={props.sessionId}
      setSessionId={props.setSessionId}
      onExit={props.logout}
      logout={props.logout}
      setFormsMetadata={props.setFormsMetadata}
      verificationError={props.verificationError}
      setVerificationError={props.setVerificationError}
      setAllQuestions={props.setAllQuestions}
    />
  );

  const formsMetadataElement = (
    <AllFormsMetadata
      formsMetadata={props.formsMetadata ?? []}
      logout={props.logout}
      setFormTemplate={props.setFormTemplate}
    />
  );

  const languagesForForm = (formId: string | undefined): string[] => {
    if (!formId) {
      return [DEFAULT_LANG];
    }
    const row = (props.formsMetadata ?? []).find((form) => String(form.form_id) === String(formId));
    return normalizeFormLanguageCodes(row?.languages);
  };

  return (
    <Routes>
      <Route path={LOGIN_APP_PATH} element={loginElement} />
      <Route path={ALL_FORMS_PATH} element={formsMetadataElement} />
      <Route
        path={`${EDIT_FORM_PATH}/:formId`}
        element={
          <EditFormRoute
            formTemplate={props.formTemplate}
            setFormTemplate={props.setFormTemplate}
            stringsByLang={props.stringsByLang}
            setStringsByLang={props.setStringsByLang}
            loadedStringsByLang={props.loadedStringsByLang}
            failedLangs={props.failedLangs}
            logout={props.logout}
            setFormsMetadata={props.setFormsMetadata}
            allQuestions={props.allQuestions}
            languagesForForm={languagesForForm}
            createForm={false}
          />
        }
      />
      <Route
        path={`${CREATE_FORM_PATH}/:formId`}
        element={
          <EditFormRoute
            formTemplate={props.formTemplate}
            setFormTemplate={props.setFormTemplate}
            stringsByLang={props.stringsByLang}
            setStringsByLang={props.setStringsByLang}
            loadedStringsByLang={props.loadedStringsByLang}
            failedLangs={props.failedLangs}
            logout={props.logout}
            setFormsMetadata={props.setFormsMetadata}
            allQuestions={props.allQuestions}
            languagesForForm={languagesForForm}
            createForm={true}
          />
        }
      />
      <Route path='/*' element={<Navigate to={LOGIN_APP_PATH} replace />} />
    </Routes>
  );
};

export default AppRoutes;
